import { createClient } from '@supabase/supabase-js'
import type { SupabaseClient, SupabaseClientOptions } from '@supabase/supabase-js'
import type { Database } from '../types/supabase'

type SupabaseAuthOptions = NonNullable<SupabaseClientOptions<'public'>['auth']>

export interface SupabaseFactoryConfig {
  /** Supabase project URL. Each app injects this from its own env. */
  url: string
  /** Supabase anon (public) key. Each app injects this from its own env. */
  anonKey: string
  /**
   * Auth session storage. Omit to use the platform default (localStorage on
   * web). On React Native, pass a SecureStore/AsyncStorage-backed adapter.
   */
  storage?: SupabaseAuthOptions['storage']
  /** Persist the auth session. Default true. */
  persistSession?: boolean
  /** Auto-refresh the access token. Default true. */
  autoRefreshToken?: boolean
  /**
   * Parse the session from the URL after OAuth/email links. Default true.
   * Set false on native (no URL-based session detection).
   */
  detectSessionInUrl?: boolean
}

/**
 * Platform-agnostic Supabase client factory. No `import.meta.env` /
 * `process.env` reads happen here — each app (web/mobile) injects its own
 * config so this stays portable across Vite and Expo. See
 * `packages/shared/README.md`.
 */
export function createSupabaseClient<DB = Database>(
  config: SupabaseFactoryConfig
): SupabaseClient<DB> {
  const {
    url,
    anonKey,
    storage,
    persistSession = true,
    autoRefreshToken = true,
    detectSessionInUrl = true,
  } = config

  return createClient<DB>(url, anonKey, {
    auth: {
      storage,
      persistSession,
      autoRefreshToken,
      detectSessionInUrl,
    },
  })
}
